import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMenu, getAllMenus, deleteMenu } from '../../services/firebaseService';
import { uploadToS3 } from '../../services/s3Service';

const initialState = {
  menus: [],
  currentMenu: null,
  templates: [
    {
      id: 'classic',
      name: 'Classic Menu',
      description: 'Traditional restaurant menu layout',
      thumbnail: '/templates/classic.png',
    },
    {
      id: 'modern',
      name: 'Modern Menu',
      description: 'Sleek and contemporary design',
      thumbnail: '/templates/modern.png',
    },
    {
      id: 'minimal',
      name: 'Minimal Menu',
      description: 'Clean and simple layout',
      thumbnail: '/templates/minimal.png',
    },
  ],
  exportStatus: 'idle', // idle, exporting, uploading, complete, error
  exportProgress: 0,
  loading: false,
  error: null,
};

// Async thunks
export const fetchMenus = createAsyncThunk(
  'menu/fetchMenus',
  async (userId, { rejectWithValue }) => {
    try {
      return new Promise((resolve, reject) => {
        getAllMenus(userId, (menus) => {
          resolve(menus);
        }, reject);
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createMenu = createAsyncThunk(
  'menu/createMenu',
  async ({ userId, menuData }, { rejectWithValue }) => {
    try {
      const menuId = await addMenu(userId, menuData);
      return { id: menuId, ...menuData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const exportAndUploadMenu = createAsyncThunk(
  'menu/exportAndUploadMenu',
  async ({ userId, menuId, menuElement, format = 'image' }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setExportStatus('exporting'));
      dispatch(setExportProgress(30));

      // Export menu as image (placeholder - implement with html-to-image)
      const blob = new Blob(['menu-export-placeholder'], { type: 'image/png' });
      const file = new File([blob], `menu-${menuId}.${format === 'image' ? 'png' : 'mp4'}`, {
        type: format === 'image' ? 'image/png' : 'video/mp4',
      });

      dispatch(setExportStatus('uploading'));
      dispatch(setExportProgress(60));

      // Upload to S3
      const s3Url = await uploadToS3(file, `menus/${userId}`);

      dispatch(setExportProgress(90));

      // Update menu record in Firebase with S3 URL
      const menuUpdate = {
        exportUrl: s3Url,
        exportedAt: new Date().toISOString(),
        format,
      };

      dispatch(setExportStatus('complete'));
      dispatch(setExportProgress(100));

      return { menuId, s3Url, ...menuUpdate };
    } catch (error) {
      dispatch(setExportStatus('error'));
      return rejectWithValue(error.message);
    }
  }
);

export const removeMenu = createAsyncThunk(
  'menu/removeMenu',
  async ({ userId, menuId }, { rejectWithValue }) => {
    try {
      await deleteMenu(userId, menuId);
      return menuId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setCurrentMenu: (state, action) => {
      state.currentMenu = action.payload;
    },
    updateCurrentMenu: (state, action) => {
      if (state.currentMenu) {
        state.currentMenu = { ...state.currentMenu, ...action.payload };
      }
    },
    clearCurrentMenu: (state) => {
      state.currentMenu = null;
    },
    setExportStatus: (state, action) => {
      state.exportStatus = action.payload;
    },
    setExportProgress: (state, action) => {
      state.exportProgress = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Menus
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.menus = action.payload;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Menu
      .addCase(createMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.menus.push(action.payload);
      })
      .addCase(createMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Export and Upload Menu
      .addCase(exportAndUploadMenu.fulfilled, (state, action) => {
        const { menuId, s3Url, exportedAt, format } = action.payload;
        const menu = state.menus.find((m) => m.id === menuId);
        if (menu) {
          menu.exportUrl = s3Url;
          menu.exportedAt = exportedAt;
          menu.format = format;
        }
      })
      // Remove Menu
      .addCase(removeMenu.fulfilled, (state, action) => {
        state.menus = state.menus.filter((menu) => menu.id !== action.payload);
      });
  },
});

export const {
  setCurrentMenu,
  updateCurrentMenu,
  clearCurrentMenu,
  setExportStatus,
  setExportProgress,
  clearError,
} = menuSlice.actions;

export default menuSlice.reducer;

